import { Component, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { StepperModule } from 'primeng/stepper';
import { ButtonModule } from 'primeng/button';

import { MemberService } from '../../services/member';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import {
  DropdownItem,
  PremiumRule,
  GENDER_OPTIONS,
  DEFAULT_NOMINATION_PERCENTAGE,
  MEMBER_PATTERNS,
  SaveMemberRequest,
  NomineeRequest,
  ageOnToday,
} from './member.model';
import { MemberDetailsStep } from './member-details-step';
import { NomineeDetailsStep } from './nominee-details-step';
import { OtpStep } from './otp-step';
import { MemberPaymentStep, MemberPaymentSummary } from './member-payment-step';

/**
 * Member onboarding wizard (parent / orchestrator).
 *
 * Owns the single root FormGroup (incl. the nominees FormArray) and ALL
 * validators, drives the 3-step p-stepper, performs the Save submission and
 * hands the OTP metadata from the Save response down to Step 3.
 *
 * Step 1 starts from a PREMIUM RULE: picking one binds its policy display,
 * fixes the member's allowed age range and feeds the live (preview-only)
 * premium breakup, which the user can't edit and which is NOT sent — the
 * backend recomputes the authoritative amounts from the rule + isDoctorConsultant.
 */
@Component({
  selector: 'app-member-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StepperModule,
    ButtonModule,
    MemberDetailsStep,
    NomineeDetailsStep,
    OtpStep,
    MemberPaymentStep,
  ],
  templateUrl: './member-form.html',
  styleUrls: ['./member-form.css'],
})
export class MemberForm implements OnInit {
  private fb = inject(FormBuilder);
  private svc = inject(MemberService);
  private toast = inject(ToastService);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  readonly pageTitle = inject(PageTitleService).title;

  /** 1 = Premium & Member, 2 = Nominee Details, 3 = OTP, 4 = Payment. Bound to <p-stepper [(value)]>. */
  activeStep = 1;

  readonly genderOptions = GENDER_OPTIONS;
  premiumRuleOptions = signal<DropdownItem[]>([]);
  relationshipOptions = signal<DropdownItem[]>([]);

  /** The currently selected rule — drives age bounds and the premium preview. */
  selectedRule = signal<PremiumRule | null>(null);
  private rulesById = new Map<number, PremiumRule>();

  saving = signal(false);

  // ---- populated from the Save response, fed into Step 3 ----
  memberId = signal<number | null>(null);
  memberUniqueId = signal<string | null>(null);
  memberSeriesNo = signal<string | null>(null);
  masterPolicyOnLoan = signal<string | null>(null);
  channels = signal<string[]>([]);
  expiresInMinutes = signal(5);
  otpSent = signal(true);
  otpMessage = signal('');

  /** Step-1 control names — used to validate Step 1 before advancing. */
  private readonly step1Controls = [
    'firstName', 'middleName', 'lastName', 'loanNumber', 'dateOfBirth', 'gender',
    'panNumber', 'aadhaarNumber', 'email', 'mobileNumber', 'address', 'city',
    'state', 'pincode', 'cflBranch', 'healthDeclaration', 'termcondition',
  ];

  form: FormGroup = this.fb.group({
    // Selected premium rule (PartnerId is derived server-side from the JWT).
    primiumrulrID: [null, Validators.required],
    // Read-only policy display, bound from the selected rule.
    masterPolicyName: [{ value: null, disabled: true }],
    coverageType: [{ value: null, disabled: true }],
    premiumCode: [{ value: null, disabled: true }],

    firstName: ['', Validators.required],
    middleName: [null],
    lastName: [null],
    fullName: [{ value: '', disabled: true }],
    loanNumber: [null],
    // The age band is checked HERE, as the date is picked, rather than only by the server on
    // save — the plan's eligible range is already known the moment a rule is chosen.
    dateOfBirth: [null, [Validators.required, this.notFuture, this.ageWithinRule.bind(this)]],
    age: [{ value: null, disabled: true }],
    gender: [null],
    // PAN stays OPTIONAL — validated only for shape when something is typed.
    panNumber: [null, Validators.pattern(MEMBER_PATTERNS.pan)],
    // Aadhaar is the KYC identifier a member is onboarded against, so it is required.
    aadhaarNumber: [null, [Validators.required, Validators.pattern(MEMBER_PATTERNS.aadhaar)]],
    email: [null, Validators.email],
    mobileNumber: ['', [Validators.required, Validators.pattern(MEMBER_PATTERNS.mobile)]],
    address: ['', Validators.required],
    city: ['', Validators.required],
    state: ['', Validators.required],
    pincode: ['', [Validators.required, Validators.pattern(MEMBER_PATTERNS.pincode)]],
    cflBranch: [null],
    isDoctorConsultant: [false],
    healthDeclaration: [false, Validators.requiredTrue],
    termcondition: [false, Validators.requiredTrue],

    // Preview-only premium breakup (disabled => never edited, never sent).
    doctorConsultantAMT: [{ value: 0, disabled: true }],
    basePrimume: [{ value: null, disabled: true }],
    gstPer: [{ value: null, disabled: true }],
    gstAmt: [{ value: null, disabled: true }],
    premiumWithGST: [{ value: null, disabled: true }],
    grossAmt: [{ value: null, disabled: true }],

    // Nominees are OPTIONAL — start with none. The user can add one or more;
    // when present, their percentages must total 100 (percentageSum validator,
    // which treats an empty array as valid).
    nominees: this.fb.array([] as FormGroup[], this.percentageSum),
  });

  ngOnInit(): void {
    // Re-bind display + recompute the preview whenever the chosen rule changes.
    this.form.get('primiumrulrID')?.valueChanges.subscribe((id) => this.applyRule(id));
    // The doctor-consultant flag is added into base before GST — recompute on toggle.
    this.form.get('isDoctorConsultant')?.valueChanges.subscribe(() => this.recomputePremium());

    if (isPlatformBrowser(this.platformId)) {
      this.svc.premiumRules().subscribe((rules) => this.setRules(rules));
      this.svc.relationships().subscribe((opts) => this.relationshipOptions.set(opts));
    }
  }

  // ===================== premium rule =====================
  private setRules(rules: PremiumRule[]): void {
    this.rulesById.clear();
    for (const r of rules) this.rulesById.set(r.premiumRuleId, r);
    this.premiumRuleOptions.set(
      rules.map((r) => ({
        value: r.premiumRuleId,
        label: `${r.masterPolicyName} — ${r.coverageType} (${r.premiumCode})`,
      })),
    );
  }

  /** Bind the chosen rule's read-only fields and refresh the premium preview. */
  private applyRule(id: number | null): void {
    const rule = id != null ? this.rulesById.get(Number(id)) ?? null : null;
    this.selectedRule.set(rule);
    this.form.patchValue(
      {
        masterPolicyName: rule?.masterPolicyName ?? null,
        coverageType: rule?.coverageType ?? null,
        premiumCode: rule?.premiumCode ?? null,
      },
      { emitEvent: false },
    );
    this.recomputePremium();

    // The band belongs to the rule, so a date that was fine under the previous plan may not be
    // under this one. Re-check it now instead of leaving a stale "valid" on screen — the rule is
    // very often picked AFTER the date of birth.
    this.form.get('dateOfBirth')?.updateValueAndValidity({ emitEvent: false });
  }

  /**
   * The date of birth must put the member inside the selected plan's age band.
   *
   * A method (not an arrow property) so it exists when the `form` field initializer runs, and
   * bound at that point so `this.selectedRule` resolves. Silent until a rule is chosen: with no
   * plan there is no band to be outside of.
   */
  private ageWithinRule(control: AbstractControl): ValidationErrors | null {
    const rule = this.selectedRule();
    if (!rule || !control.value) return null;

    const age = ageOnToday(control.value);
    if (age == null) return null;

    // A zero bound means "unbounded" — the same reading the server applies.
    if (rule.ageFrom > 0 && age < rule.ageFrom) return { ageBelow: { age, min: rule.ageFrom } };
    if (rule.ageTo > 0 && age > rule.ageTo) return { ageAbove: { age, max: rule.ageTo } };
    return null;
  }

  /**
   * Live premium preview from the selected rule + isDoctorConsultant. The doctor
   * amount is ADDED into base, then GST is applied on the new base. Display only.
   */
  private recomputePremium(): void {
    const rule = this.selectedRule();
    if (!rule) {
      this.form.patchValue(
        { doctorConsultantAMT: 0, basePrimume: null, gstPer: null, gstAmt: null, premiumWithGST: null, grossAmt: null },
        { emitEvent: false },
      );
      return;
    }
    const isDoc = !!this.form.get('isDoctorConsultant')?.value;
    const doctorConsultantAMT = isDoc ? rule.doctorConsultant : 0;
    const basePrimume = this.round2(rule.basePremium + doctorConsultantAMT);
    const gstPer = rule.gstPercentage;
    const gstAmt = this.round2((basePrimume * gstPer) / 100);
    const premiumWithGST = this.round2(basePrimume + gstAmt);
    this.form.patchValue(
      {
        doctorConsultantAMT: this.round2(doctorConsultantAMT),
        basePrimume,
        gstPer,
        gstAmt,
        premiumWithGST,
        grossAmt: premiumWithGST,
      },
      { emitEvent: false },
    );
  }

  private round2(n: number): number {
    return Math.round((n + Number.EPSILON) * 100) / 100;
  }

  // ===================== nominees (FormArray) =====================
  get nominees(): FormArray {
    return this.form.get('nominees') as FormArray;
  }

  /** A fresh nominee group — the single source of truth for a nominee's shape.
   *  `sameAsMember` is UI-only (not sent): when on, the nominee's mobile/address/email mirror
   *  the member's and are locked; turning it off unlocks them for manual entry. */
  private newNominee(): FormGroup {
    // Only the FIRST nominee defaults to 100% — later ones start at 0 so adding a second
    // nominee doesn't instantly break the must-total-100% rule (100 + 100 = unsaveable form).
    const defaultPct = this.nominees.length === 0 ? DEFAULT_NOMINATION_PERCENTAGE : 0;
    return this.fb.group({
      nomineeName: ['', Validators.required],
      dateOfBirth: [null, [Validators.required, this.notFuture]],
      relationship: [null, Validators.required],
      nominationPercentage: [defaultPct, [Validators.min(0), Validators.max(100)]],
      sameAsMember: [true],
      address: ['', Validators.required],
      mobileNumber: ['', [Validators.required, Validators.pattern(MEMBER_PATTERNS.mobile)]],
      email: [null, Validators.email],
    });
  }

  addNominee(): void {
    const g = this.newNominee();
    this.nominees.push(g);
    this.applyMemberContact(g, true); // defaults to "same as member" — prefill + lock
  }

  removeNominee(index: number): void {
    // Nominees are optional, so removing the last one is allowed.
    this.nominees.removeAt(index);
  }

  /** Child reported a "same as member" checkbox change for the nominee at `index`. */
  onNomineeSameToggle(index: number): void {
    const g = this.nominees.at(index) as FormGroup;
    this.applyMemberContact(g, !!g.get('sameAsMember')?.value);
  }

  /**
   * Mirror the member's mobile/address/email into a nominee group and LOCK those controls when
   * "same as member" is on; ENABLE them for manual entry when off. Disabled controls keep their
   * value (the member's) and are still included via getRawValue() in buildRequest().
   */
  private applyMemberContact(group: FormGroup, sameAsMember: boolean): void {
    const mobile = group.get('mobileNumber')!;
    const address = group.get('address')!;
    const email = group.get('email')!;
    if (sameAsMember) {
      const m = this.form.getRawValue();
      group.patchValue(
        { mobileNumber: m.mobileNumber ?? '', address: m.address ?? '', email: m.email ?? null },
        { emitEvent: false },
      );
      mobile.disable({ emitEvent: false });
      address.disable({ emitEvent: false });
      email.disable({ emitEvent: false });
    } else {
      mobile.enable({ emitEvent: false });
      address.enable({ emitEvent: false });
      email.enable({ emitEvent: false });
    }
  }

  // ===================== validators =====================
  /** Reject a date that is after today (DOB / nominee DOB can't be in the future). */
  private notFuture(c: AbstractControl): ValidationErrors | null {
    if (!c.value) return null;
    const d = c.value instanceof Date ? c.value : new Date(c.value);
    if (isNaN(d.getTime())) return null;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return d.getTime() > today.getTime() ? { future: true } : null;
  }

  /** FormArray validator: the nominees' percentages must total exactly 100. */
  private percentageSum(arr: AbstractControl): ValidationErrors | null {
    const controls = (arr as FormArray).controls;
    if (!controls.length) return null;
    const total = controls.reduce(
      (s, g) => s + (Number(g.get('nominationPercentage')?.value) || 0),
      0,
    );
    return total === 100 ? null : { percentageSum: true };
  }

  // ===================== validation =====================
  /**
   * First Step-1 problem as a user-facing message, or null when Step 1 is valid.
   * Optional fields (middle/last name, CFL branch, gender, PAN, Aadhaar, email)
   * are only flagged when present-but-malformed — blank is always allowed.
   */
  private firstStep1Error(): string | null {
    const f = this.form;
    if (f.get('primiumrulrID')?.invalid) return 'Please select a premium rule.';
    if (f.get('firstName')?.invalid) return 'First name is required.';
    if (f.get('dateOfBirth')?.errors?.['required']) return 'Date of birth is required.';
    if (f.get('dateOfBirth')?.errors?.['future']) return 'Date of birth cannot be in the future.';

    // Age must fall within the selected rule's [AgeFrom, AgeTo].
    const rule = this.selectedRule();
    const age = Number(f.get('age')?.value);
    if (rule && Number.isFinite(age) && (age < rule.ageFrom || age > rule.ageTo)) {
      return `Age must be between ${rule.ageFrom} and ${rule.ageTo} for the selected premium rule.`;
    }

    if (f.get('panNumber')?.invalid) return 'PAN must look like ABCDE1234F.';
    if (f.get('aadhaarNumber')?.invalid) return 'Aadhaar must be exactly 12 digits.';
    if (f.get('email')?.invalid) return 'Please enter a valid email address.';
    if (f.get('mobileNumber')?.invalid) return 'Mobile number must be exactly 10 digits.';
    if (f.get('address')?.invalid) return 'Address is required.';
    if (f.get('city')?.invalid) return 'City is required.';
    if (f.get('state')?.invalid) return 'State is required.';
    if (f.get('pincode')?.invalid) return 'Pincode must be exactly 6 digits.';
    if (f.get('healthDeclaration')?.invalid) return 'Please accept the health declaration.';
    if (f.get('termcondition')?.invalid) return 'Please accept the Terms & Conditions.';
    return null;
  }

  /** First nominee problem as a user-facing message, or null when all are valid. */
  private firstNomineeError(): string | null {
    const arr = this.nominees;
    for (let i = 0; i < arr.length; i++) {
      const g = arr.at(i);
      const who = `Nominee ${i + 1}`;
      if (g.get('nomineeName')?.invalid) return `${who}: name is required.`;
      if (g.get('dateOfBirth')?.errors?.['required']) return `${who}: date of birth is required.`;
      if (g.get('dateOfBirth')?.errors?.['future']) return `${who}: date of birth cannot be in the future.`;
      if (g.get('relationship')?.invalid) return `${who}: relationship is required.`;
      if (g.get('mobileNumber')?.invalid) return `${who}: mobile must be exactly 10 digits.`;
      if (g.get('address')?.invalid) return `${who}: address is required.`;
      if (g.get('email')?.invalid) return `${who}: please enter a valid email.`;
    }
    if (arr.errors?.['percentageSum']) {
      return 'Nomination percentages across all nominees must total 100%.';
    }
    return null;
  }

  /** Mark Step 1 touched + toast the first problem; true when Step 1 is valid. */
  private validateStep1(): boolean {
    this.form.get('primiumrulrID')?.markAsTouched();
    for (const name of this.step1Controls) this.form.get(name)?.markAsTouched();
    const err = this.firstStep1Error();
    if (err) {
      this.toast.warning(err);
      return false;
    }
    return true;
  }

  /** Mark nominees touched + toast the first problem; true when all are valid. */
  private validateNominees(): boolean {
    this.nominees.markAllAsTouched();
    const err = this.firstNomineeError();
    if (err) {
      this.toast.warning(err);
      return false;
    }
    return true;
  }

  // ===================== step navigation =====================
  /** Validate Step 1, then advance to Step 2. */
  goToNominees(): void {
    if (!this.validateStep1()) return;
    // Re-mirror the member's contact into any "same as member" nominees, in case Step 1 was
    // edited after a nominee was added (e.g. the user went back and changed the mobile/address).
    for (const ctrl of this.nominees.controls) {
      const g = ctrl as FormGroup;
      if (g.get('sameAsMember')?.value) this.applyMemberContact(g, true);
    }
    this.activeStep = 2;
  }

  back(step: number): void {
    this.activeStep = step;
  }

  // ===================== save =====================
  /** Validate the WHOLE form (Step 1 + nominees), POST /Member/Save, then go to OTP. */
  save(): void {
    // Re-check Step 1 — if anything there is invalid, send the user back to fix it.
    if (!this.validateStep1()) {
      this.activeStep = 1;
      return;
    }
    // Then the nominees (required fields + percentages total 100).
    if (!this.validateNominees()) {
      return;
    }

    const req = this.buildRequest();
    this.saving.set(true);
    this.svc.save(req).subscribe({
      next: (res: any) => {
        this.saving.set(false);
        if (res?.success !== true) {
          this.toast.error(res?.message || 'Failed to save member.');
          return;
        }
        const data = res.data ?? {};
        this.memberId.set(data.memberId ?? null);
        this.memberUniqueId.set(data.memberUniqueId ?? null);
        this.memberSeriesNo.set(data.memberSeriesNo ?? null);
        this.masterPolicyOnLoan.set(data.masterPolicyOnLoan ?? null);
        this.channels.set(data.otp?.channels ?? []);
        this.expiresInMinutes.set(data.otp?.expiresInMinutes ?? 5);
        this.otpSent.set(data.otpSent ?? false);
        this.otpMessage.set(data.otpMessage ?? '');

        this.toast.success(res.message || 'Member saved. OTP sent.');
        if (this.memberId() != null) {
          this.activeStep = 3;
        }
      },
      error: (err: any) => {
        this.saving.set(false);
        const problem = err?.error;
        // ASP.NET ValidationProblemDetails -> { errors: { Field: [msg, ...] } }.
        const fieldErrors =
          problem?.errors && typeof problem.errors === 'object'
            ? Object.entries(problem.errors)
                .map(([field, msgs]) => `${field}: ${(msgs as string[]).join(', ')}`)
                .join(' | ')
            : null;
        // Full body to the console so the failing field is always visible.
        console.error('Member/Save failed:', problem ?? err);
        this.toast.error(
          fieldErrors || problem?.message || problem?.title || 'Failed to save member.',
        );
      },
    });
  }

  /** Build the SaveMemberRequest — only the rule id, member fields, doctor flag, nominees. */
  private buildRequest(): SaveMemberRequest {
    const v = this.form.getRawValue();
    const nominees: NomineeRequest[] = (v.nominees ?? []).map((n: any) => ({
      nomineeName: n.nomineeName,
      dateOfBirth: this.toIso(n.dateOfBirth),
      relationship: n.relationship,
      nominationPercentage: Number(n.nominationPercentage) || 0,
      address: n.address,
      mobileNumber: n.mobileNumber,
      email: n.email || null,
    }));

    return {
      primiumrulrID: v.primiumrulrID,
      firstName: v.firstName,
      // Plain optional text fields are non-nullable strings server-side: send ''
      // (not null) when blank so model binding doesn't reject them.
      middleName: v.middleName ?? '',
      lastName: v.lastName ?? '',
      gender: v.gender ?? '',
      cflBranch: v.cflBranch ?? '',
      loanNumber: v.loanNumber || null, // blank => backend auto-generates (nullable)
      dateOfBirth: this.toIso(v.dateOfBirth),
      age: v.age ?? null,
      // Format-validated optionals stay null when blank — null skips the
      // PAN/Aadhaar/email validators, whereas '' would fail their format check.
      panNumber: v.panNumber ?? null,
      aadhaarNumber: v.aadhaarNumber ?? null,
      email: v.email ?? null,
      mobileNumber: v.mobileNumber,
      address: v.address,
      city: v.city,
      state: v.state,
      pincode: v.pincode,
      healthDeclaration: !!v.healthDeclaration,
      termcondition: !!v.termcondition,
      isDoctorConsultant: !!v.isDoctorConsultant,
      nominees,
    };
  }

  /** Format a Date (or date string) as yyyy-MM-dd for the API. */
  private toIso(value: any): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // ===================== OTP verified =====================
  /** KYC verified — advance to the in-wizard Payment step (Step 4). */
  onVerified(): void {
    if (this.memberId() == null) {
      this.router.navigate(['/dashboard']);
      return;
    }
    this.activeStep = 4;
  }

  /** Computed gross premium, passed to the Payment step as the amount prefill. */
  grossAmt(): number | null {
    const v = Number(this.form.get('grossAmt')?.value);
    return Number.isFinite(v) && v > 0 ? v : null;
  }

  /** Member's display name for the Razorpay Checkout prefill. */
  prefillName(): string {
    const f = this.form.getRawValue();
    return [f.firstName, f.lastName].filter(Boolean).join(' ');
  }

  /** Build the review summary (member details + nominees + premium breakup) for Step 4. */
  paymentSummary(): MemberPaymentSummary {
    const f = this.form.getRawValue();
    const relLabel = (v: any) =>
      this.relationshipOptions().find((o) => o.value === v)?.label ?? (v ? String(v) : '—');
    const fmt = (d: any) => {
      if (!d) return '—';
      const x = d instanceof Date ? d : new Date(d);
      return isNaN(x.getTime())
        ? '—'
        : x.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };
    return {
      policyName: f.masterPolicyName || '—',
      coverageType: f.coverageType || '—',
      premiumCode: f.premiumCode || '—',
      fullName: [f.firstName, f.middleName, f.lastName].filter(Boolean).join(' ') || '—',
      dob: fmt(f.dateOfBirth),
      gender: f.gender || '—',
      mobile: f.mobileNumber || '—',
      email: f.email || '—',
      pan: f.panNumber || '—',
      address: [f.address, f.city, f.state, f.pincode].filter(Boolean).join(', ') || '—',
      base: Number(f.basePrimume) || 0,
      doctor: Number(f.doctorConsultantAMT) || 0,
      gstPer: Number(f.gstPer) || 0,
      gst: Number(f.gstAmt) || 0,
      total: Number(f.grossAmt) || 0,
      nominees: (f.nominees ?? []).map((n: any) => ({
        name: n.nomineeName || '—',
        relationship: relLabel(n.relationship),
        dob: fmt(n.dateOfBirth),
        percentage: Number(n.nominationPercentage) || 0,
        mobile: n.mobileNumber || '—',
      })),
    };
  }

  /** Reset the whole wizard back to Step 1 to onboard another member. */
  restart(): void {
    this.memberId.set(null);
    this.memberUniqueId.set(null);
    this.memberSeriesNo.set(null);
    this.masterPolicyOnLoan.set(null);
    this.channels.set([]);
    this.otpMessage.set('');
    this.selectedRule.set(null);

    this.form.reset({ isDoctorConsultant: false, termcondition: false });
    const arr = this.nominees;
    while (arr.length) arr.removeAt(0); // nominees optional — leave empty

    this.activeStep = 1;
  }
}
